import React, { useEffect, useState, useCallback, useMemo, useRef } from "react"
import { useSelector } from "react-redux"
import { Container, Pagination } from "react-bootstrap"
import {
  useQueryParam, StringParam, NumberParam, withDefault
} from "use-query-params"
import { roleCheck } from "utils/index"
import { OPERATION_MANAGER, ACCOUNTS_MANAGER } from "utils/constants"

import SearchInput from "components/SearchInput"
import FilterDropdown from "components/FilterDropdown"
import Loader from "components/Loader"
import { getGears } from "requesters/gearRequester"
import { Link } from "react-router-dom"
import AnchorButton from "components/AnchorButton"
import { getBaseUrl, getCurrentUser } from "../../../reducers"

import GearsTable from "./GearsTable"

export default function Gears() {
  const baseUrl = useSelector(getBaseUrl)
  const { gearStatus } = useSelector(state => state.options)
  const [userOptions, setUserOptions] = useState([])

  const [byStatus, setByStatus] = useQueryParam("by_status", StringParam)
  const [bySearch, setBySearch] = useQueryParam("by_search", StringParam)
  const [byUser, setByUser] = useQueryParam("by_user", StringParam)
  const [page, setPage] = useQueryParam("gears_page", withDefault(NumberParam, 1))
  const [perPage, setPerPage] = useQueryParam("gears_per_page", withDefault(StringParam, "25"))

  const [gears, setGears] = useState({})
  const [gearIds, setGearIds] = useState([])

  const [requesting, setRequesting] = useState(false)
  const [listMeta, setListMeta] = useState(null)
  const filterMountRef = useRef(true)
  const currentUser = useSelector(getCurrentUser)
  const systemRole = currentUser.attributes.role
  const currentTeamRole = currentUser.attributes.current_team_role

  const rolesAllowed = () => {
    if (roleCheck(systemRole, currentTeamRole, OPERATION_MANAGER) || roleCheck(systemRole, currentTeamRole, ACCOUNTS_MANAGER)) {
      return true
    }
    return false
  }

  const loadUserFilterOptions = useCallback(async () => {
    const { response, error } = await getGears(baseUrl, {})
    if (response) {
      const gearsMap = _.get(response, "gear", {})
      const byUserId = new Map()
      Object.values(gearsMap).forEach(gear => {
        const rawUserId = _.get(gear, "attributes.userId")
        const userId = rawUserId != null ? String(rawUserId) : null
        const fullName = _.get(gear, "attributes.userName")
        if (!userId || !fullName) return
        if (byUserId.has(userId)) return
        byUserId.set(userId, {
          fullName,
          surname: _.get(gear, "attributes.userSurname") || "",
          firstname: _.get(gear, "attributes.userFirstname") || ""
        })
      })
      const compareByFirstnameThenSurname = (a, b) => {
        const fir = String(a.firstname).localeCompare(String(b.firstname), undefined, { sensitivity: "base", numeric: true })
        if (fir !== 0) return fir
        const sur = String(a.surname).localeCompare(String(b.surname), undefined, { sensitivity: "base", numeric: true })
        if (sur !== 0) return sur
        return String(a.fullName).localeCompare(String(b.fullName), undefined, { sensitivity: "base", numeric: true })
      }
      const sorted = Array.from(byUserId.values()).sort(compareByFirstnameThenSurname)
      const seenDisplayNames = new Set()
      const options = []
      sorted.forEach(({ fullName }) => {
        const label = fullName.trim()
        if (!label || seenDisplayNames.has(label)) return
        seenDisplayNames.add(label)
        options.push([label, label])
      })
      setUserOptions(options)
    }
    if (error) {
      console.error(error)
    }
  }, [baseUrl])

  const fetchData = useCallback(async () => {
    const [tableResult] = await Promise.all([
      getGears(baseUrl, {
        by_status: byStatus,
        by_keyword: bySearch,
        by_user: byUser,
        page,
        per_page: perPage
      }),
      loadUserFilterOptions()
    ])
    const { response, error, meta } = tableResult
    if (response) {
      setGears(_.get(response, "gear", {}))
      setGearIds(_.get(response, "meta.meta.meta.ids", []))
      const p = meta?.pagination
      if (p) {
        setListMeta({
          total_count: p.total_count,
          total_pages: p.total_pages,
          current_page: p.current_page,
          per_page: p.per_page,
          show_all: !!p.show_all
        })
      }
    }
    if (error) {
      console.error(error)
    }
  }, [baseUrl, byStatus, bySearch, byUser, page, perPage, loadUserFilterOptions])

  useEffect(() => {
    if (filterMountRef.current) {
      filterMountRef.current = false
      return
    }
    setPage(1)
  }, [byStatus, bySearch, byUser, setPage])

  useEffect(() => {
    if (!rolesAllowed()) {
      window.location = "/"
      return
    }
    setRequesting(true)
    fetchData().finally(() => setRequesting(false))
  }, [fetchData])

  const clearFilters = () => {
    setBySearch(undefined)
    setByStatus(undefined)
    setByUser(undefined)
    setPage(1)
    setPerPage("25")
  }

  const byStatusName = useMemo(
    () => gearStatus.find(([, k]) => k === byStatus)?.[0],
    [gearStatus, byStatus]
  )

  return (
    <Container fluid className="manage-leads-page">
      <div className="container-inner">
        <div className="d-flex justify-content-between align-items-center header-tabs-container">
          <div className="header-tabs">
            <h1>Company Assets</h1>
          </div>
          <Link
            className="btn btn-outlined-blue"
            to="/settings/gears/new"
            data-target="#newEquipmentModal"
            data-toggle="modal"
            type="button">
            + New Asset
          </Link>
        </div>
        <div className="table-listing-filters">
          <div className="btn-group mr-2">
            <SearchInput value={bySearch} setValue={setBySearch} placeholder="Search by description or category" />
          </div>
          <div className="btn-group mr-2">
            <FilterDropdown
              title="Status"
              filteredName={byStatusName}
              setFilter={setByStatus}
              options={gearStatus} />
          </div>
          <div className="btn-group mr-2">
            <FilterDropdown
              title="User"
              filteredName={byUser}
              setFilter={setByUser}
              options={userOptions} />
          </div>
          <div className="btn-group">
            <AnchorButton
              className="f-small"
              onClick={clearFilters}>
              Clear&#xa0;Filters
            </AnchorButton>
          </div>
        </div>
        {!requesting && listMeta && (
          <div className="d-flex flex-wrap align-items-center justify-content-between mt-3 mb-2 px-1">
            {listMeta.total_count > 0 && (
              <div className="text-muted small mb-2 mb-md-0">
                {listMeta.show_all
                  ? `Showing all ${listMeta.total_count} assets`
                  : (() => {
                    const start = (listMeta.current_page - 1) * listMeta.per_page + 1
                    const end = Math.min(listMeta.current_page * listMeta.per_page, listMeta.total_count)
                    return `Showing ${start}–${end} of ${listMeta.total_count} assets`
                  })()}
              </div>
            )}
            <div className="d-flex align-items-center flex-wrap">
              <label className="mr-2 mb-0 text-muted small" htmlFor="gears-per-page">Rows per page</label>
              <select
                id="gears-per-page"
                className="custom-select custom-select-sm mr-md-3 mb-2 mb-md-0"
                style={{ width: "auto" }}
                value={perPage}
                onChange={e => {
                  setPerPage(e.target.value)
                  setPage(1)
                }}>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="all">Show all</option>
              </select>
              {!listMeta.show_all && listMeta.total_pages > 1 && (
                <Pagination className="mb-0">
                  <Pagination.Prev
                    disabled={listMeta.current_page <= 1}
                    onClick={() => setPage(Math.max(1, listMeta.current_page - 1))} />
                  <Pagination.Item disabled>
                    {listMeta.current_page}
                    {" "}
                    /
                    {listMeta.total_pages}
                  </Pagination.Item>
                  <Pagination.Next
                    disabled={listMeta.current_page >= listMeta.total_pages}
                    onClick={() => setPage(listMeta.current_page + 1)} />
                </Pagination>
              )}
            </div>
          </div>
        )}
        {requesting ? <Loader /> : <GearsTable gearIds={gearIds} gears={gears} fetchData={fetchData} />}
      </div>
    </Container>
  )
}
